/**
 * Utility functions for simulation API calls with usage limit handling
 */

import { apiErrorMessage, isUpgradeRequired } from './apiErrorMessage';

const API_URL = import.meta.env.VITE_API_URL || '';

/**
 * Said only when the server refuses for a spent quota but names no reason. Its own sentence rather than the
 * old `${payload} Please upgrade your plan to continue.` suffix, which produced
 * "[object Object] Please upgrade your plan to continue." for the shape the routes actually send, and
 * duplicated the advice for the shapes where they do say it.
 */
const QUOTA_EXHAUSTED_MESSAGE =
  "You have used your plan's allowance for this period. Please upgrade to continue.";

export interface UsageLimitError {
  error: string;
  upgrade_required: boolean;
}

export interface SimulationResponse {
  simulation_id: number;
  message: string;
  status: string;
}

export class UsageLimitExceededError extends Error {
  public readonly upgrade_required = true;

  constructor(message: string) {
    super(message);
    this.name = 'UsageLimitExceededError';
  }
}

/**
 * Create a new simulation with proper error handling for usage limits
 */
export const createSimulation = async (simulationData: any): Promise<SimulationResponse> => {
  const response = await fetch(`${API_URL}/simulations`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify(simulationData),
  });

  const data = await response.json();

  if (!response.ok) {
    // `upgrade_required` arrives NESTED inside `detail` (routes_fastapi/simulation.py:390 raises
    // HTTPException(403, detail={...})). Reading only the top level is why the sixth-simulation refusal came
    // back as a plain Error and left the Run button enabled.
    if (isUpgradeRequired(data)) {
      throw new UsageLimitExceededError(apiErrorMessage(data, QUOTA_EXHAUSTED_MESSAGE));
    }
    throw new Error(apiErrorMessage(data, 'Failed to start simulation'));
  }

  return data;
};

/**
 * Rerun an existing simulation with proper error handling for usage limits
 */
export const rerunSimulation = async (simulationId: number): Promise<SimulationResponse> => {
  const response = await fetch(`${API_URL}/simulations/${simulationId}/rerun`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
  });

  const data = await response.json();

  if (!response.ok) {
    if (isUpgradeRequired(data)) {
      throw new UsageLimitExceededError(apiErrorMessage(data, QUOTA_EXHAUSTED_MESSAGE));
    }
    throw new Error(apiErrorMessage(data, 'Failed to rerun simulation'));
  }

  return data;
};

export type SurveyMode = 'ai_sim' | 'human';

/**
 * Parse questions with proper error handling for usage limits.
 *
 * Pass surveyMode='human' from the human-survey paste-raw-text flow
 * (SurveyBuilder) so the backend uses the human Pydantic schema (which
 * includes dropdown / image_hotspot / photo_upload + UX-only fields).
 * AI-simulation callers leave the default 'ai_sim'.
 */
export const parseQuestions = async (
  questions: string[],
  goal: string = '',
  context: string = '',
  question_images?: Record<number, string[]>,
  advanced_parsing: boolean = false,
  question_types?: Record<number, string>,
  surveyMode: SurveyMode = 'ai_sim',
  two_pass_parsing: boolean = false,
): Promise<{ task_id: string; questions_count: number }> => {
  const response = await fetch(`${API_URL}/parse-questions`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify({
      questions,
      goal,
      context,
      question_images,
      advanced_parsing,
      question_types,
      survey_mode: surveyMode,
      two_pass_parsing,
    }),
  });

  const data = await response.json();

  if (!response.ok) {
    if (isUpgradeRequired(data)) {
      throw new UsageLimitExceededError(apiErrorMessage(data, QUOTA_EXHAUSTED_MESSAGE));
    }
    throw new Error(apiErrorMessage(data, `HTTP ${response.status}: ${response.statusText}`));
  }

  return data;
};

export interface QuestionTag {
  id: string;
  start_idx: number;
  end_idx: number;
  anchor_start: string;
  anchor_end: string;
  type: string;
  needs_image: boolean;
  image_level: 'question' | 'option' | 'none';
}

/**
 * Real-time question splitting + tagging via Claude Haiku.
 * Sends raw unsplit text; AI identifies question boundaries and returns
 * character offsets, anchor words, type, and image-need flag.
 */
export const tagQuestionsRealtime = async (
  rawText: string,
  goal: string = '',
  context: string = '',
  signal?: AbortSignal,
  surveyMode: SurveyMode = 'ai_sim',
): Promise<{ success: boolean; questions: QuestionTag[] }> => {
  const response = await fetch(`${API_URL}/parse-questions/tag-realtime`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ raw_text: rawText, goal, context, survey_mode: surveyMode }),
    signal,
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(apiErrorMessage(data, `HTTP ${response.status}`));
  }

  return data;
};

/**
 * Unified realtime parsing: Haiku split + boundary resolution + parallel Sonnet parse.
 * Returns fully-parsed QuestionSchema[] directly (no Celery / WebSocket).
 */
export const parseQuestionsRealtime = async (
  rawText: string,
  goal: string = '',
  context: string = '',
  signal?: AbortSignal,
  surveyMode: SurveyMode = 'ai_sim',
  twoPassParsing: boolean = true,
): Promise<{ success: boolean; questions: ParsedQuestionFromAPI[] }> => {
  const response = await fetch(`${API_URL}/parse-questions/parse-realtime`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({
      raw_text: rawText,
      goal,
      context,
      survey_mode: surveyMode,
      two_pass_parsing: twoPassParsing,
    }),
    signal,
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(apiErrorMessage(data, `HTTP ${response.status}`));
  }

  return data;
};

/**
 * SSE streaming two-pass parsing.
 * Pass 1: Returns boundaries immediately (frontend shows skeletons)
 * Pass 2: Streams questions as they complete (out of order)
 */
export interface StreamingParseCallbacks {
  onBoundaries: (boundaries: Array<{ s: number; e: number; t: string }>) => void;
  onQuestion: (index: number, question: ParsedQuestionFromAPI) => void;
  onComplete: () => void;
  onError: (error: string) => void;
  // Emitted (when present) before any question events in sectioned surveys.
  onSections?: (sections: ParsedSectionFromAPI[]) => void;
}

export const parseQuestionsRealtimeStream = (
  rawText: string,
  goal: string = '',
  context: string = '',
  callbacks: StreamingParseCallbacks,
  surveyMode: SurveyMode = 'ai_sim',
): { abort: () => void } => {
  console.log('[parseQuestionsRealtimeStream] Called with text length:', rawText.length);
  const abortController = new AbortController();

  // Use fetch with streaming for SSE (EventSource doesn't support POST)
  const startStream = async () => {
    console.log(
      '[parseQuestionsRealtimeStream] Starting fetch to:',
      `${API_URL}/parse-questions/parse-realtime-stream`,
    );
    try {
      const response = await fetch(`${API_URL}/parse-questions/parse-realtime-stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ raw_text: rawText, goal, context, survey_mode: surveyMode }),
        signal: abortController.signal,
      });

      console.log('[parseQuestionsRealtimeStream] Response status:', response.status);
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(apiErrorMessage(errorData, `HTTP ${response.status}`));
      }

      const reader = response.body?.getReader();
      if (!reader) throw new Error('No response body');
      console.log('[parseQuestionsRealtimeStream] Got reader, starting to read chunks...');

      const decoder = new TextDecoder();
      let buffer = '';
      // Track event/data across chunks (they may arrive separately)
      let currentEvent = '';
      let currentData = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        console.log(
          '[parseQuestionsRealtimeStream] Buffer content:',
          JSON.stringify(buffer.substring(0, 200)),
        );

        // Parse SSE events from buffer
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer
        console.log(
          '[parseQuestionsRealtimeStream] Split into',
          lines.length,
          'lines, remaining:',
          buffer.length,
        );

        for (const rawLine of lines) {
          const line = rawLine.replace(/\r$/, ''); // Strip trailing \r from Windows-style \r\n
          console.log(
            '[parseQuestionsRealtimeStream] Line:',
            JSON.stringify(line.substring(0, 80)),
          );
          if (line.startsWith('event:')) {
            currentEvent = line.slice(6).trim();
            console.log('[parseQuestionsRealtimeStream] Set event:', currentEvent);
          } else if (line.startsWith('data:')) {
            currentData = line.slice(5).trim();
            console.log('[parseQuestionsRealtimeStream] Set data length:', currentData.length);
          } else if (line === '' && currentEvent && currentData) {
            console.log('[parseQuestionsRealtimeStream] Empty line with event+data, dispatching!');
            // End of event, dispatch it
            console.log('[SSE] Dispatching event:', currentEvent, currentData.substring(0, 100));
            try {
              const data = JSON.parse(currentData);
              switch (currentEvent) {
                case 'sections':
                  console.log('[SSE] onSections called with', data.sections?.length, 'sections');
                  callbacks.onSections?.(data.sections || []);
                  break;
                case 'boundaries':
                  console.log(
                    '[SSE] onBoundaries called with',
                    data.boundaries.length,
                    'boundaries',
                  );
                  callbacks.onBoundaries(data.boundaries);
                  break;
                case 'question':
                  console.log('[SSE] onQuestion called for index', data.index);
                  callbacks.onQuestion(data.index, data.question);
                  break;
                case 'complete':
                  console.log('[SSE] onComplete called');
                  callbacks.onComplete();
                  break;
                case 'error':
                  console.log('[SSE] onError called:', data.message);
                  callbacks.onError(data.message);
                  break;
              }
            } catch (e) {
              console.warn('Failed to parse SSE data:', currentData, e);
            }
            currentEvent = '';
            currentData = '';
          }
        }
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        return; // Aborted, don't report as error
      }
      callbacks.onError(error instanceof Error ? error.message : 'Stream failed');
    }
  };

  startStream();

  return {
    abort: () => abortController.abort(),
  };
};

export interface ParsedQuestionFromAPI {
  id?: string;
  text: string;
  type: string;
  choices?: string[];
  scale_min?: number;
  scale_max?: number;
  max_selections?: number;
  ranking_count?: number;
  text_limit?: number;
  allow_other?: boolean;
  image_paths?: string[];
  image_descriptions?: string[];
  image_attachment_level?: string;
  option_images?: Record<string, string>;
  matrix_rows?: string[];
  matrix_columns?: string[];
  maxdiff_items?: string[];
  conjoint_attributes?: Array<{ name: string; levels: string[] }>;
  conjoint_description?: string;
  description?: string;
  // Followup detection
  is_followup?: boolean;
  followup_reason?: string;
  // Section / stimulus grouping
  section_id?: string;
  section_title?: string;
  section_context?: string;
}

export interface ParsedSectionFromAPI {
  id: string;
  title?: string | null;
  context: string;
  start_index: number;
  num_questions: number;
}

/**
 * Handle API errors consistently across forms
 */
export const handleApiError = (error: unknown, setError: (message: string) => void): void => {
  // Goes through the one serialiser rather than three branches: the `else` used to discard a raw payload
  // entirely (a rejected promise carrying a parsed body, not an Error, told the user only "an unexpected
  // error occurred"), and `apiErrorMessage` reads Error, string and payload alike.
  setError(apiErrorMessage(error, 'An unexpected error occurred. Please try again.'));
};

/**
 * Generate AI descriptions for a list of image paths
 * Used when users edit images in parsed questions
 */
export const generateImageDescriptions = async (
  imagePaths: string[],
): Promise<{
  success: boolean;
  descriptions: string[];
  processed_count: number;
}> => {
  const response = await fetch(`${API_URL}/api/question-images/generate-descriptions`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify({ image_paths: imagePaths }),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(apiErrorMessage(data, 'Failed to generate descriptions'));
  }

  return data;
};
